import { FilterInput } from '@kinetic-ui/data-layer';
import { UpdateConfig } from 'components/Table';
import { useEffect } from 'react';
import { PREFERENCE_KEYS } from 'types';
import { useSessionFilterContext } from '../contexts/SessionFilterContext';
import { useFiltersContext } from '../contexts/filtersContext';
import { useSavedFilterContext } from '../contexts/savedFiltersContext';
import { useUpdateColumnsFromPreference } from './useUpdateColumnsFromPreference';

type FilterInputMode = {
  mode: 'active' | 'default' | 'session';
  initialValues: FilterInput[];
  initialColumns?: UpdateConfig[] | null;
};

type EmptyMode = {
  mode: 'empty';
};

type NotReadyMode = {
  mode: 'notReady';
};

type InitialValuesReturn = FilterInputMode | EmptyMode | NotReadyMode;

export const useFiltersInitializationType = ({
  preferenceKey,
}: {
  preferenceKey: PREFERENCE_KEYS;
}): InitialValuesReturn => {
  const { getSessionFilterValue } = useSessionFilterContext();
  const {
    savedFilters: { activeSavedFilterId, value, orgValue, defaultSavedFilterId, loading },
  } = useSavedFilterContext();

  if (loading) {
    return {
      mode: 'notReady',
    };
  }

  const sessionValues = getSessionFilterValue(preferenceKey);
  if (sessionValues) {
    return {
      mode: 'session',
      initialValues: sessionValues,
    };
  }

  if (activeSavedFilterId) {
    const activeSavedFilter = value?.[activeSavedFilterId] ?? orgValue?.[activeSavedFilterId];
    if (activeSavedFilter) {
      const activeFilterValues = activeSavedFilter.filters;
      const activeColumns = activeSavedFilter.columns ?? null;
      return {
        mode: 'active',
        initialValues: activeFilterValues,
        initialColumns: activeColumns,
      };
    }
  }

  if (defaultSavedFilterId) {
    const defaultSavedFilter = value?.[defaultSavedFilterId] ?? orgValue?.[defaultSavedFilterId];
    if (defaultSavedFilter) {
      const defaultFilterValues = defaultSavedFilter.filters;
      const defaultColumns = defaultSavedFilter.columns ?? null;
      return {
        mode: 'default',
        initialValues: defaultFilterValues,
        initialColumns: defaultColumns,
      };
    }
  }

  return {
    mode: 'empty',
  };
};

export const useInitializeFilterValues = () => {
  const {
    form,
    filtersPreferenceKey: preferenceKey,
    toForm,
    formLoaded: ready,
    filterInputs,
    populateForm,
    filtersInitialized,
    setFiltersInitialized,
  } = useFiltersContext();

  const {
    savedFilters: { setActiveSavedFilter, defaultSavedFilterId },
  } = useSavedFilterContext();

  const init = useFiltersInitializationType({ preferenceKey });
  const { setSessionFilterValue } = useSessionFilterContext();

  const [, setValues] = form.values;
  const { updateColumnsFromPreference } = useUpdateColumnsFromPreference();

  useEffect(
    () => {
      // wait until the preferences have loaded, and the form has called onChange with its initial values
      if (init.mode === 'notReady' || !ready) {
        return;
      }

      // bail out if we've already initialized
      if (filtersInitialized) {
        return;
      }

      if (init.mode === 'active' || init.mode === 'session') {
        const { initialValues, initialColumns } = init;
        const formValues = toForm(initialValues);
        setValues(formValues);
        populateForm(initialValues);
        if (initialColumns) {
          updateColumnsFromPreference(initialColumns);
        }
      }
      if (init.mode === 'default') {
        const { initialColumns } = init;
        setActiveSavedFilter(defaultSavedFilterId);
        if (initialColumns) {
          updateColumnsFromPreference(initialColumns);
        }
      }

      // init.mode === 'empty': hack to trigger a call to onChange after we've initialized
      if (init.mode === 'empty') {
        setValues((p) => ({ ...p }));
      }

      setFiltersInitialized(true);
    },
    // only run when the init mode changes (from notReady to some ready-ish type)
    // and only run after the form is ready (it has called onChange once)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [init.mode, ready]
  );

  useEffect(() => {
    // we only want to set the session value if we are already initialized
    if (filtersInitialized) {
      setSessionFilterValue(preferenceKey, filterInputs);
    }
  }, [filterInputs, filtersInitialized, preferenceKey, setSessionFilterValue]);
};
